// actions
const SET = "invoices/SET";
const SET_SEARCH_TEXT = "invoices/SET_SEARCH_TEXT";
const SET_SELECTED = "invoices/SET_SELECTED";
const SET_INVOICES_STATS = "invoices/SET_INVOICES_STATS";
const SET_CREDIT_NOTES_STATS = "invoices/SET_CREDIT_NOTES_STATS";

const CHANGE_LIMIT = "invoices/CHANGE_LIMIT";
const NEXT_ACTION = "invoices/NEXT_ACTION";
const SET_NEXT_TOKEN = "invoices/SET_NEXT_TOKEN";
const SET_PREVIOUS_TOKENS = "invoices/SET_PREVIOUS_TOKENS";
const SET_CARRY_OVER_LISTING = "invoices/SET_CARRY_OVER_LISTING";
const SET_INVOICE_LIST_RESUME = "invoices/SET_INVOICE_LIST_RESUME";
const HYDRATE_PAGINATION = "invoices/HYDRATE_PAGINATION";

export type InvoiceListResumeState = {
  pageIndex: number;
  inputCursor: string | null;
  limit?: number;
  scopeKey?: string;
} | null;

const DEFAULT_STATE = {
  listing: [],
  invoicesStats: [],
  creditNotesStats: [],
  searchText: "",
  selected: null,

  limit: 100,
  nextToken: null,
  previousTokens: [],
  carryOverListing: [] as any[],
  /** Exact GraphQL nextToken used to load the current page (forward paging); avoids drift when merge/chase burns extra requests. */
  invoiceListResume: null as InvoiceListResumeState,
};

// reducer
const reducer = (state = DEFAULT_STATE, action: any = {}) => {
  switch (action.type) {
    case SET:
      return Object.assign({}, state, { listing: action.listing });
    case SET_INVOICES_STATS:
      return Object.assign({}, state, { invoicesStats: action.listing });
    case SET_CREDIT_NOTES_STATS:
      return Object.assign({}, state, { creditNotesStats: action.listing });

    case SET_SEARCH_TEXT:
      return Object.assign({}, state, { searchText: action.searchText });
    case SET_SELECTED:
      return Object.assign({}, state, { selected: action.model });

    case SET_NEXT_TOKEN:
      return Object.assign({}, state, { nextToken: action.nextToken });
    case SET_PREVIOUS_TOKENS:
      return Object.assign({}, state, {
        previousTokens: action.previousTokens,
      });

    case CHANGE_LIMIT:
      return Object.assign({}, state, {
        limit: action.limit,
        nextToken: action.nextToken,
        nextNextToken: action.nextNextToken,
        previousTokens: action.previousTokens,
        carryOverListing: [],
        invoiceListResume: null,
      });

    case SET_CARRY_OVER_LISTING:
      return Object.assign({}, state, {
        carryOverListing: action.listing,
      });

    case SET_INVOICE_LIST_RESUME:
      return Object.assign({}, state, {
        invoiceListResume: action.invoiceListResume,
      });

    case HYDRATE_PAGINATION:
      return Object.assign({}, state, {
        limit: action.limit,
        nextToken: action.nextToken,
        previousTokens: Array.isArray(action.previousTokens)
          ? action.previousTokens
          : [],
        invoiceListResume:
          action.invoiceListResume !== undefined
            ? action.invoiceListResume
            : null,
      });

    case NEXT_ACTION:
      return Object.assign({}, state, {
        nextToken: action.nextToken,
        previousTokens: action.previousTokens,
      });

    default:
      return state;
  }
};

export default reducer;

// action creators
export const setListing = (listing: any[]) => ({ listing, type: SET });

export const setListingInvoicesStats = (listing: any[]) => ({
  listing,
  type: SET_INVOICES_STATS,
});

export const setListingCreditNotesStats = (listing: any[]) => ({
  listing,
  type: SET_CREDIT_NOTES_STATS,
});

export const setSearchText = (searchText: string) => ({
  searchText,
  type: SET_SEARCH_TEXT,
});

export const setSelected = (model: any) => ({ model, type: SET_SELECTED });

export const changeLimit = (limit: any) => ({
  limit: limit,
  nextToken: null,
  nextNextToken: null,
  previousTokens: [],

  type: CHANGE_LIMIT,
});

export const nextAction = (nextToken: any, previousTokens: any[]) => ({
  nextToken: nextToken,
  previousTokens: previousTokens ? [...previousTokens, nextToken] : [nextToken],

  type: NEXT_ACTION,
});

export const setCarryOverListing = (listing: any[]) => ({
  listing,
  type: SET_CARRY_OVER_LISTING,
});

export const setInvoiceListResume = (
  invoiceListResume: InvoiceListResumeState
) => ({
  invoiceListResume,
  type: SET_INVOICE_LIST_RESUME,
});

export const setNextToken = (nextToken: any) => ({
  nextToken,
  type: SET_NEXT_TOKEN,
});

export const setPreviousToken = (previousTokens: any) => ({
  previousTokens,
  type: SET_PREVIOUS_TOKENS,
});

/** Restore invoice list cursor chain after navigating back (GraphQL paging is token-based). */
export const hydrateInvoicePagination = (payload: {
  limit: number;
  nextToken: string | null;
  previousTokens: any[];
  invoiceListResume?: InvoiceListResumeState;
}) => ({
  ...payload,
  type: HYDRATE_PAGINATION,
});
